import {
  ConfigurableColumn,
  getConfigurableColumns,
  getEffectiveColumnOrder,
  isConfigurable,
} from '@experiences/components/table/blocs/utils/configurable-columns-utils';
import { ConfigurableColumnDef } from '@experiences/components/table/types';
import {
  ConfigurationDomain,
  TableName,
} from '@/domain/configuration/blocs/stores/column-preferences-keys';
import {
  EMPTY_COLUMNS_PREFERENCES,
  useColumnPreferencesStore,
} from '@/domain/configuration/blocs/stores/column-preferences-store';
import { useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { VisibilityState } from '@tanstack/react-table';

interface UseConfigureColumnsResult {
  columnOrder: string[];
  columnVisibility: VisibilityState;
  configurableColumns: ConfigurableColumn[];
  configurableColumnsOrder: string[];
  reorderColumns: (configurableOrder: string[]) => void;
  resetToDefaults: () => void;
  toggleColumnVisibility: (columnId: string) => void;
}

export const useConfigureColumns = (
  domain: ConfigurationDomain,
  table: TableName,
  columns: ConfigurableColumnDef[],
): UseConfigureColumnsResult => {
  const { columnsPreferences, reorder, reset, toggle } =
    useColumnPreferencesStore(
      useShallow((state) => ({
        columnsPreferences:
          state.preferences[domain][table]?.columns ??
          EMPTY_COLUMNS_PREFERENCES,
        reorder: state.reorderColumns,
        reset: state.resetToDefaults,
        toggle: state.toggleColumnVisibility,
      })),
    );
  const { order: storedColumnOrder, visibility: storedVisibility } =
    columnsPreferences;

  const configurableColumns = useMemo(
    () => getConfigurableColumns(columns),
    [columns],
  );

  const configurableIds = useMemo(
    () => configurableColumns.map((column) => column.id),
    [configurableColumns],
  );

  const configurableColumnsOrder = useMemo(
    () => getEffectiveColumnOrder(storedColumnOrder, configurableIds),
    [configurableIds, storedColumnOrder],
  );

  const columnOrder = useMemo(() => {
    const queue = [...configurableColumnsOrder];
    const result: string[] = [];
    for (const column of columns) {
      if (!column.id) {
        continue;
      }
      if (isConfigurable(column)) {
        const next = queue.shift();
        if (next) {
          result.push(next);
        }
      } else {
        result.push(column.id);
      }
    }
    result.push(...queue);
    return result;
  }, [columns, configurableColumnsOrder]);

  const defaultsHidden = useMemo(() => {
    const map: Record<string, boolean> = {};
    for (const column of columns) {
      if (column.id && column.meta?.defaultHidden) {
        map[column.id] = true;
      }
    }
    return map;
  }, [columns]);

  const columnVisibility = useMemo<VisibilityState>(() => {
    const visibility: VisibilityState = {};
    for (const column of columns) {
      const id = column.id;
      if (!id || !isConfigurable(column)) {
        continue;
      }
      const explicit = storedVisibility[id];
      const visible = explicit ?? !defaultsHidden[id];
      if (!visible) {
        visibility[id] = false;
      }
    }
    return visibility;
  }, [columns, storedVisibility, defaultsHidden]);

  const toggleColumnVisibility = useCallback(
    (columnId: string) => {
      const explicit =
        useColumnPreferencesStore.getState().preferences[domain][table]?.columns
          ?.visibility[columnId];
      toggle(domain, table, columnId, explicit ?? !defaultsHidden[columnId]);
    },
    [domain, table, toggle, defaultsHidden],
  );

  const reorderColumns = useCallback(
    (configurableOrder: string[]) => {
      const configurableSet = new Set(configurableIds);
      const filtered = configurableOrder.filter((id) =>
        configurableSet.has(id),
      );
      reorder(domain, table, filtered);
    },
    [domain, table, reorder, configurableIds],
  );

  const resetToDefaults = useCallback(() => {
    reset(domain, table);
  }, [domain, table, reset]);

  return useMemo(
    () => ({
      columnOrder,
      columnVisibility,
      configurableColumns,
      configurableColumnsOrder,
      reorderColumns,
      resetToDefaults,
      toggleColumnVisibility,
    }),
    [
      columnOrder,
      columnVisibility,
      configurableColumns,
      configurableColumnsOrder,
      reorderColumns,
      resetToDefaults,
      toggleColumnVisibility,
    ],
  );
};
